/**
 * SYSTEM SETTINGS MODULE — Incentives.
 * Company-level configuration for the Incentive Tracker module. Workspace
 * owner/admin only (route is RequireWorkspaceAdmin). Holds the "require manager
 * review" policy and the account-level Sales Manager who reviews incentive
 * programs when review is required.
 */

import { useEffect, useState } from "react";
import { getIncentiveConfig, setRequireReview, setSalesManager } from "../utils/incentiveSettingsService.js";
import { getMyPrimaryAccount, listMembers } from "../../../shared/utils/accountService.js";

export default function IncentivesSection() {
  const [requireReview, setReview] = useState(false);
  const [salesManager, setSM] = useState("");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const acct = await getMyPrimaryAccount();
        const [cfg, roster] = await Promise.all([
          getIncentiveConfig(),
          acct?.accountId ? listMembers(acct.accountId) : Promise.resolve([]),
        ]);
        if (!alive) return;
        setReview(cfg.requireReview);
        setSM(cfg.salesManagerUserId || "");
        setMembers(roster || []);
      } catch (e) { if (alive) setErr(e.message); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  function flashSaved() { setSavedFlash(true); setTimeout(() => setSavedFlash(false), 1800); }

  async function toggle(e) {
    const next = e.target.checked;
    setReview(next); setErr(null); setSavedFlash(false);
    try { await setRequireReview(next); flashSaved(); }
    catch (e) { setReview(!next); setErr(e.message); }
  }

  async function changeSalesManager(e) {
    const next = e.target.value;
    const prev = salesManager;
    setSM(next); setErr(null); setSavedFlash(false);
    try { await setSalesManager(next); flashSaved(); }
    catch (e) { setSM(prev); setErr(e.message); }
  }

  if (loading) return <section className="settings-card wide"><p className="muted">Loading incentive settings…</p></section>;

  return (
    <section className="settings-card wide">
      <h2 style={{ marginTop: 0 }}>Incentives</h2>
      <p className="muted">
        Company-wide settings for the Incentive Tracker. Only workspace owners and
        admins can change these.
      </p>

      {err && <p className="error">{err}</p>}

      <label style={{ display: "flex", flexDirection: "row", alignItems: "flex-start", gap: 10, fontSize: 14, marginTop: 8 }}>
        <input type="checkbox" checked={requireReview} onChange={toggle} style={{ width: "auto", marginTop: 3 }} />
        <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span>Require manager review before sending</span>
          <span className="muted" style={{ fontSize: 13 }}>
            When on, a rep's incentive program must be approved by the Sales Manager
            before it can be sent to a distributor. When off, reps can mark a
            program as sent directly from draft.
          </span>
        </span>
      </label>

      <div style={{ marginTop: 20, maxWidth: 460 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 14 }}>
          <span>Sales Manager <span className="muted">(reviews incentive programs)</span></span>
          <select value={salesManager} onChange={changeSalesManager} disabled={members.length === 0}>
            <option value="">No Sales Manager</option>
            {members.map((m) => (
              <option key={m.user_id} value={m.user_id}>{m.email}</option>
            ))}
          </select>
        </label>
        <p className="muted" style={{ fontSize: 13, marginTop: 6 }}>
          The team member who receives an incentive program for review when review is
          required. They (or any owner/admin) can approve or reject it.
        </p>
      </div>

      {savedFlash && <p className="muted" style={{ color: "var(--success)", marginTop: 12 }}>Saved.</p>}
    </section>
  );
}
